import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap'
import * as moment from 'moment'
import { BUS_SLOTS, EVENT_PERIOD_END_UTC, EVENT_PERIOD_START_UTC } from './busSlots'
import './BusAgendaModal.css'

const isWithinEventPeriod = () => {
  const now = moment.utc()
  const startDate = moment.utc(EVENT_PERIOD_START_UTC)
  const endDate = moment.utc(EVENT_PERIOD_END_UTC)
  return now.isBetween(startDate, endDate, null, '[]')
}

const BusAgendaModal = () => {
  const [showModal, setShowModal] = useState(false)
  const [officeLocation, setOfficeLocation] = useState('')
  const [busCity, setBusCity] = useState('')
  const [departureTime, setDepartureTime] = useState('')
  const [returnTime, setReturnTime] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const citySlots = useMemo(() => (busCity ? BUS_SLOTS[busCity] : null), [busCity])

  const canSubmit =
    !!busCity && !!departureTime && !!returnTime && !isSubmitting

  useEffect(() => {
    const checkBusAgenda = async () => {
      try {
        const response = await axios.get('/bus-agenda/check')
        if (response.data.showModal && response.data.busCity) {
          setShowModal(true)
          setOfficeLocation(response.data.officeLocation || '')
          setBusCity(response.data.busCity)
        }
      } catch (error) {
        console.error('Error checking bus agenda', error)
      }
    }

    if (isWithinEventPeriod()) {
      checkBusAgenda()
    }
  }, [])

  const handleSubmit = async () => {
    if (!canSubmit) {
      return
    }

    setIsSubmitting(true)
    try {
      await axios.post('/bus-agenda/submit', {
        uses_bus: true,
        departure_time: departureTime,
        return_time: returnTime
      })
      toast.success('Zgjedhja juaj për autobusin u regjistrua me sukses.')
      setShowModal(false)
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        'Ndodhi një gabim gjatë regjistrimit të zgjedhjes.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDecline = async () => {
    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)
    try {
      await axios.post('/bus-agenda/submit', { uses_bus: false })
      toast.info('Ju nuk do të përdorni autobusin për HAJKO FEEEST 2026.')
      setShowModal(false)
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        'Ndodhi një gabim gjatë regjistrimit të përgjigjes.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderTimeOptions = (times, selectedValue, onSelect) => (
    <div className='bus-agenda-time-grid'>
      {times.map((time) => (
        <button
          key={time}
          type='button'
          className={`bus-agenda-time-chip ${selectedValue === time ? 'is-selected' : ''}`}
          onClick={() => onSelect(time)}
          disabled={isSubmitting}
        >
          <span>{time}</span>
          {selectedValue === time && <i className='fa fa-check' aria-hidden='true' />}
        </button>
      ))}
    </div>
  )

  return (
    <Modal
      className='bus-agenda-modal modal-xl modal-dialog-centered'
      isOpen={!!showModal}
      backdrop='static'
      keyboard={false}
    >
      <ModalBody className='bus-agenda-body'>
        <div className='survey-step'>
          <div className='survey-step-intro'>
            <h2 className='survey-step-title'>HAJKO FEEEST 2026</h2>
            <p className='survey-step-subtitle'>Orari i autobusit</p>
            <p className='award-step-description'>
              Ju lutemi zgjidhni orarin e nisjes dhe kthimit. Kjo na ndihmon ta organizojme transportin me sakte.
            </p>
          </div>

          <div className='bus-agenda-info-cards'>
            <div className='bus-agenda-info-card'>
              <span className='bus-agenda-label'>Lokacioni juaj i zyres</span>
              <strong>{officeLocation || '-'}</strong>
            </div>
            <div className='bus-agenda-info-card'>
              <span className='bus-agenda-label'>Qyteti i autobusit</span>
              <strong>{busCity || '-'}</strong>
            </div>
            <div className='bus-agenda-info-card'>
              <span className='bus-agenda-label'>Pickup/Drop-off</span>
              <strong>{citySlots?.pickupDropoff || '-'}</strong>
            </div>
          </div>

          {citySlots && (
            <div className='bus-agenda-slots'>
              <section className='bus-agenda-slot-section'>
                <h4 className='bus-agenda-slot-title'>Nisjet</h4>
                {renderTimeOptions(citySlots.departures, departureTime, setDepartureTime)}
              </section>

              <section className='bus-agenda-slot-section'>
                <h4 className='bus-agenda-slot-title'>Kthimet</h4>
                {renderTimeOptions(citySlots.returns, returnTime, setReturnTime)}
              </section>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className='bus-agenda-footer'>
        <div className='bus-agenda-actions'>
          <Button
            className='btn-block bus-agenda-confirm-btn'
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Duke regjistruar...' : 'Konfirmo zgjedhjen'}
          </Button>
          <Button
            className='btn-block bus-agenda-decline-btn'
            disabled={isSubmitting}
            onClick={handleDecline}
          >
            Nuk do të përdor autobusin
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default BusAgendaModal
